<template>
    <v-dialog v-model="editInstanceDialog" max-width="600" persistent>
        <template v-slot:activator="{ on: dialog, attrs }">
            <template v-if="$scopedSlots.activator">
                <slot name="activator" :on="dialog" :attrs="attrs" :disabled="isFixedInstance" :open="openDialog"></slot>
            </template>
            <template v-else>
                <v-tooltip left>
                    <template v-slot:activator="{ on: tooltip }">
                        <v-btn v-if="isFixedInstance" small class="mr-1" icon disabled v-bind="attrs">
                            <v-icon small>mdi-pencil-outline</v-icon>
                        </v-btn>
                        <v-btn v-else small class="mr-1" icon v-bind="attrs" v-on="{ ...tooltip, ...dialog }">
                            <v-icon small>mdi-pencil-outline</v-icon>
                        </v-btn>
                    </template>
                    <span>Edit</span>
                </v-tooltip>
            </template>
        </template>
        <v-card flat>
            <v-card-title class="flex-nowrap align-start">
                <div class="d-flex align-center secondary--text">
                    <span>Instance Details</span>
                </div>

                <v-spacer></v-spacer>
                <v-btn :disabled="saving" @click="editInstanceDialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text class="mt-4">
                <v-form ref="editForm" v-model="formValid" @submit.prevent="saveInstance">
                    <v-row no-gutters>
                        <v-col cols="12" class="mb-4">
                            <v-text-field
                                v-model="editedInstanceName"
                                label="Name"
                                outlined
                                dense
                                :rules="[rules.required]"
                                :counter="1023"
                                :disabled="saving"
                                required></v-text-field>
                        </v-col>
                        <v-col cols="12" class="mb-4">
                            <v-textarea
                                v-model="editedInstanceDescription"
                                label="Description"
                                outlined
                                dense
                                :rules="[rules.required]"
                                :counter="1023"
                                :disabled="saving"
                                rows="3"
                                auto-grow></v-textarea>
                        </v-col>
                    </v-row>
                </v-form>
                <v-alert v-if="error" color="error" icon="warning" class="mb-0" text>
                    {{ errorContent }}
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="success" v-if="showSuccess"><v-icon>mdi-check</v-icon></v-btn>
                <v-btn v-else :disabled="!formValid || !hasChanges || saving" :loading="saving" color="primary" text @click="saveInstance">Save Changes</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { enumsData } from '@/mixins/enums'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as instanceErrors from '@/modules/instance/errors'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'

const { getBackendErrorMessage } = createBackendErrorHandler(instanceErrors, lifecycleErrors, permissionsErrors)

export default {
    name: 'InstanceEditDialog',
    mixins: [enumsData],
    props: {
        instance: {
            type: Object,
            required: true
        },
        spaceId: {
            type: [String, Number],
            default: null
        }
    },
    data() {
        return {
            error: false,
            errorContent: '',
            showSuccess: false,
            saving: false,
            editInstanceDialog: false,
            formValid: false,
            editedInstanceName: '',
            editedInstanceDescription: '',
            rules: {
                required: v => !!v || 'Field is required.'
            }
        }
    },
    computed: {
        instanceName() {
            return this.instance?.long_id || ''
        },
        instanceDescription() {
            return this.instance?.description || ''
        },
        isFixedInstance() {
            return this.instanceName && [this.instanceFixedNames.MASTER_INSTANCE, this.instanceFixedNames.DISTRIBUTED_INSTANCE].includes(this.instanceName)
        },
        hasChanges() {
            return this.editedInstanceName !== this.instanceName || this.editedInstanceDescription !== this.instanceDescription
        },
        resolvedSpaceId() {
            return this.spaceId || this.$route.params.sid
        }
    },
    watch: {
        editInstanceDialog(newVal) {
            if (newVal) {
                this.initializeForm()
            } else {
                this.resetForm()
            }
        }
    },
    methods: {
        openDialog() {
            if (this.isFixedInstance) {
                return
            }
            this.editInstanceDialog = true
        },
        initializeForm() {
            this.editedInstanceName = this.instanceName
            this.editedInstanceDescription = this.instanceDescription
            this.error = false
            this.showSuccess = false

            // Reset form validation
            this.$nextTick(() => {
                if (this.$refs.editForm) {
                    this.$refs.editForm.resetValidation()
                }
            })
        },

        resetForm() {
            this.editedInstanceName = ''
            this.editedInstanceDescription = ''
            this.error = false
            this.showSuccess = false
            this.saving = false
        },

        cancelEdit() {
            this.editInstanceDialog = false
        },

        async saveInstance() {
            if (!this.hasChanges) {
                this.editInstanceDialog = false
                return
            }

            this.saving = true
            this.error = false

            try {
                const payload = {
                    long_id: this.editedInstanceName,
                    description: this.editedInstanceDescription
                }

                await this.$axios.patch(`/instances/${this.instance.iid}`, payload)
                this.showSuccess = true
                setTimeout(() => {
                    this.editInstanceDialog = false
                    if (this.resolvedSpaceId) {
                        this.$store.dispatch('spaceStore/fetchSpaceInstances', this.resolvedSpaceId)
                    }
                    this.$emit('updated')
                }, 2000)
            } catch (error) {
                console.error('Error updating instance:', error)
                this.errorContent = getBackendErrorMessage(error, 'Failed to update instance. If the problem persists, contact support.')
                this.error = !!this.errorContent
            } finally {
                this.saving = false
            }
        }
    }
}
</script>
